import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudies } from '../../hooks/useStudies';
import { useSurveys } from '../../hooks/useSurveys';
import SurveyScheduleBuilder from '../../components/studies/SurveyScheduleBuilder';
import { StudyType, StudyTimepoint, CreateStudyFormData } from '../../types/study.types';

type WizardStep = 'basics' | 'schedule' | 'settings' | 'review';

const CreateStudy: React.FC = () => {
  const navigate = useNavigate();
  const { createStudy, loading: creatingStudy, error: createError } = useStudies();
  const { listSurveys, loading: loadingSurveys } = useSurveys();

  const [currentStep, setCurrentStep] = useState<WizardStep>('basics');
  const [availableSurveys, setAvailableSurveys] = useState<{ id: string; title: string }[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  const [studyType, setStudyType] = useState<StudyType>('observational');
  const [timepoints, setTimepoints] = useState<StudyTimepoint[]>([]);
  const [targetEnrollment, setTargetEnrollment] = useState(100);
  const [reminderDays, setReminderDays] = useState<number[]>([3, 7]);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [consentText, setConsentText] = useState(
    'I confirm that I have read and understood the participant information sheet and have had the opportunity to ask questions. I understand that my participation is voluntary and that I am free to withdraw at any time.'
  );

  // Load available surveys
  useEffect(() => {
    const loadSurveys = async () => {
      try {
        const surveys = await listSurveys({ isActive: true });
        setAvailableSurveys(surveys.map((s) => ({ id: s.id!, title: s.title })));
      } catch (err) {
        console.error('Failed to load surveys:', err);
      }
    };
    loadSurveys();
  }, [listSurveys]);

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'basics', label: 'Basic Info' },
    { key: 'schedule', label: 'Survey Schedule' },
    { key: 'settings', label: 'Settings' },
    { key: 'review', label: 'Review' },
  ];

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep);

  const validateBasics = (): boolean => {
    if (!name.trim()) {
      setError('Study name is required');
      return false;
    }
    if (!codePrefix.trim()) {
      setError('Code prefix is required');
      return false;
    }
    if (codePrefix.length < 2 || codePrefix.length > 10) {
      setError('Code prefix must be 2-10 characters');
      return false;
    }
    if (!/^[A-Z0-9]+$/i.test(codePrefix)) {
      setError('Code prefix can only contain letters and numbers');
      return false;
    }
    setError(null);
    return true;
  };

  const validateSchedule = (): boolean => {
    if (timepoints.length === 0) {
      setError('At least one timepoint is required');
      return false;
    }
    const hasBaseline = timepoints.some((tp) => tp.isBaseline);
    if (!hasBaseline) {
      setError('A baseline timepoint is required');
      return false;
    }
    const missingSurveys = timepoints.filter((tp) => !tp.surveyId);
    if (missingSurveys.length > 0) {
      setError('All timepoints must have a survey assigned');
      return false;
    }
    setError(null);
    return true;
  };

  const validateSettings = (): boolean => {
    if (targetEnrollment < 1) {
      setError('Target enrollment must be at least 1');
      return false;
    }
    setError(null);
    return true;
  };

  const goToNextStep = () => {
    let isValid = true;
    if (currentStep === 'basics') isValid = validateBasics();
    else if (currentStep === 'schedule') isValid = validateSchedule();
    else if (currentStep === 'settings') isValid = validateSettings();

    if (isValid) {
      const nextIndex = currentStepIndex + 1;
      if (nextIndex < steps.length) {
        setCurrentStep(steps[nextIndex].key);
      }
    }
  };

  const goToPrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].key);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateBasics() || !validateSchedule() || !validateSettings()) {
      return;
    }

    try {
      const studyData: CreateStudyFormData = {
        title: name.trim(),
        description: description.trim(),
        protocolId: '',
        ethicsApprovalId: '',
        type: studyType,
        codePrefix: codePrefix.toUpperCase().trim(),
        targetSampleSize: targetEnrollment,
        surveySchedule: timepoints,
      };

      const studyId = await createStudy(studyData);
      navigate(`/studies/${studyId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create study');
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/studies')}
          className="text-blue-600 hover:text-blue-800 text-sm mb-2"
        >
          ← Back to Studies
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create New Study</h1>
        <p className="text-sm text-gray-600">Set up your observational study in a few simple steps</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    index < currentStepIndex
                      ? 'bg-teal-600 text-white'
                      : index === currentStepIndex
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index < currentStepIndex ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={`mt-2 text-xs ${
                    index <= currentStepIndex ? 'text-gray-900 font-medium' : 'text-gray-500'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    index < currentStepIndex ? 'bg-teal-600' : 'bg-gray-200'
                  }`}
                ></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {(error || createError) && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error || createError}
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        {/* Step 1: Basics */}
        {currentStep === 'basics' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Study Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Endometriosis Quality of Life Study"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description of the study purpose..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code Prefix <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={codePrefix}
                onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase"
                placeholder="e.g., ENDO"
              />
              <p className="mt-1 text-xs text-gray-500">
                Participant codes will be: {codePrefix || 'PREFIX'}-001, {codePrefix || 'PREFIX'}-002, etc.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Study Type</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setStudyType('observational')}
                  className={`p-4 border-2 rounded-lg text-left transition-all cursor-pointer ${
                    studyType === 'observational'
                      ? 'border-teal-600 bg-teal-100 ring-2 ring-teal-600 ring-offset-2'
                      : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${studyType === 'observational' ? 'text-teal-700' : 'text-gray-900'}`}>
                      Observational
                    </span>
                    {studyType === 'observational' && (
                      <svg className="h-5 w-5 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className={`text-xs mt-1 ${studyType === 'observational' ? 'text-teal-600' : 'text-gray-500'}`}>
                    Sequential codes (ENDO-001, ENDO-002...). Admin assigns codes after consent.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setStudyType('rct')}
                  className={`p-4 border-2 rounded-lg text-left transition-all cursor-pointer ${
                    studyType === 'rct'
                      ? 'border-purple-600 bg-purple-100 ring-2 ring-purple-600 ring-offset-2'
                      : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${studyType === 'rct' ? 'text-purple-700' : 'text-gray-900'}`}>
                      Randomized (RCT)
                    </span>
                    {studyType === 'rct' && (
                      <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className={`text-xs mt-1 ${studyType === 'rct' ? 'text-purple-600' : 'text-gray-500'}`}>
                    Random codes generated in batches. For blinded studies.
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Survey Schedule */}
        {currentStep === 'schedule' && (
          <div>
            {loadingSurveys ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-3 text-sm text-gray-600">Loading available surveys...</p>
              </div>
            ) : (
              <>
                {/* Warning banner when no surveys available */}
                {availableSurveys.length === 0 && (
                  <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-start">
                      <svg
                        className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-800">No published surveys available</p>
                        <p className="text-xs text-yellow-600 mt-1">
                          You can still set up your timepoint schedule below. You'll need to create and publish surveys before finalizing the study.
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              // Open survey builder in new tab to preserve study creation state
                              window.open('/admin?section=builder', '_blank');
                            }}
                            className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center"
                          >
                            Open Survey Builder in new tab
                            <svg className="ml-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const surveys = await listSurveys({ isActive: true });
                                setAvailableSurveys(surveys.map((s) => ({ id: s.id!, title: s.title })));
                              } catch (err) {
                                console.error('Failed to reload surveys:', err);
                              }
                            }}
                            className="text-xs text-gray-600 hover:text-gray-800 font-medium inline-flex items-center"
                          >
                            <svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Refresh surveys list
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <SurveyScheduleBuilder
                  timepoints={timepoints}
                  onChange={setTimepoints}
                  availableSurveys={availableSurveys}
                />
              </>
            )}
          </div>
        )}

        {/* Step 3: Settings */}
        {currentStep === 'settings' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Enrollment
              </label>
              <input
                type="number"
                min="1"
                value={targetEnrollment}
                onChange={(e) => setTargetEnrollment(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                The target number of participants for this study
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reminder Days
              </label>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 5, 7, 14].map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      setReminderDays((prev) =>
                        prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
                      );
                    }}
                    className={`px-3 py-1 rounded-full text-sm ${
                      reminderDays.includes(day)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Day {day}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Send reminder emails on these days after survey becomes due
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendWelcomeEmail}
                  onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Send welcome email on enrollment</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consent Acknowledgment Text
              </label>
              <textarea
                value={consentText}
                onChange={(e) => setConsentText(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                This text will be shown to participants during enrollment. They must acknowledge it to proceed.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 'review' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Study Overview</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Name</dt>
                  <dd className="font-medium text-gray-900">{name}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Type</dt>
                  <dd className="font-medium text-gray-900">
                    {studyType === 'rct' ? 'Randomized Controlled Trial' : 'Observational'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Code Prefix</dt>
                  <dd className="font-medium text-gray-900">{codePrefix}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Target Enrollment</dt>
                  <dd className="font-medium text-gray-900">{targetEnrollment}</dd>
                </div>
              </dl>
              {description && (
                <div className="mt-3">
                  <dt className="text-gray-500 text-sm">Description</dt>
                  <dd className="text-sm text-gray-900 mt-1">{description}</dd>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">
                Survey Schedule ({timepoints.length} timepoints)
              </h3>
              <div className="space-y-2">
                {timepoints.map((tp) => (
                  <div key={tp.id} className="flex justify-between items-center text-sm">
                    <span>
                      {tp.name} {tp.isBaseline && <span className="text-blue-600">(Baseline)</span>}
                    </span>
                    <span className="text-gray-500">
                      Day {tp.daysFromEnrollment} •{' '}
                      {availableSurveys.find((s) => s.id === tp.surveyId)?.title || 'Unknown survey'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Settings</h3>
              <ul className="text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className={sendWelcomeEmail ? 'text-green-600' : 'text-gray-400'}>
                    {sendWelcomeEmail ? '✓' : '✗'}
                  </span>
                  Welcome email on enrollment
                </li>
                <li>
                  Reminder emails: Day {reminderDays.join(', Day ')}
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> The study will be created in <strong>Draft</strong> status. You
                will need to activate it before participants can enroll.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-6 flex justify-between">
        <button
          type="button"
          onClick={goToPrevStep}
          disabled={currentStepIndex === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/studies')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          {currentStep === 'review' ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={creatingStudy}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {creatingStudy ? 'Creating...' : 'Create Study'}
            </button>
          ) : (
            <button
              type="button"
              onClick={goToNextStep}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateStudy;
